"use client";

import { SWRConfig } from "swr";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

/**
 * Global SWR config + localStorage-backed cache.
 *
 * Why: SWR's default cache is in-memory only, so hard-refreshing the
 * page (or opening a new tab) throws away every cached response. On
 * admin list pages that the owner revisits constantly (orders,
 * appointments, users, pickups) that meant staring at the skeleton
 * every morning even though the data from yesterday was still fine as
 * a first paint.
 *
 * This persists the SWR map to localStorage under `swr-cache` and
 * rehydrates it on mount. Scoped to admin / driver / reseller URLs so
 * customer sessions don't lug the admin cache around in their own
 * localStorage.
 *
 * Only keys starting with `/api/admin/`, `/api/orders`, `/api/appointments`,
 * `/api/service-pickup`, `/api/gift-cards`, `/api/coupons` etc. are
 * persisted — anything else (product searches, customer-session data)
 * stays memory-only.
 */

const CACHE_KEY = "swr-cache";
const MAX_ENTRIES = 120;
const ADMIN_KEY_PREFIXES = [
  "/api/admin/",
  "/api/orders",
  "/api/appointments",
  "/api/service-pickup",
  "/api/gift-cards",
  "/api/coupons",
  "/api/discount-rules",
  "/api/wig-assets",
  "/api/resellers",
  "/api/consignments",
  "/api/call-logs",
  "/api/waitlist",
  "/api/audit-log",
];

function shouldPersist(key: string): boolean {
  return ADMIN_KEY_PREFIXES.some((p) => key.startsWith(p));
}

// Staff portals whose pages may consume the persisted cache. These render
// only after mount (no SSR/hydration of their subtree) — see below.
const PORTAL_PATH_PREFIXES = ["/admin", "/driver", "/reseller"];

function isPortalPath(pathname: string | null): boolean {
  return !!pathname && PORTAL_PATH_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p + "/"),
  );
}

type CacheEntry = [string, unknown];

function loadCache(): Map<string, unknown> {
  if (typeof window === "undefined") return new Map();
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return new Map();
    const entries = JSON.parse(raw) as CacheEntry[];
    return new Map(entries);
  } catch {
    // Malformed cache — wipe it rather than throw.
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch {
      /* noop */
    }
    return new Map();
  }
}

function persistCache(map: Map<string, unknown>): void {
  if (typeof window === "undefined") return;
  try {
    // Cap the number of persisted entries — admin URLs with pagination
    // + filters can generate 100+ distinct keys over a session. FIFO
    // order via Map iteration is acceptable (oldest entry evicted first).
    const persistable: CacheEntry[] = [];
    for (const [key, value] of map) {
      if (!shouldPersist(key)) continue;
      persistable.push([key, value]);
    }
    const trimmed = persistable.slice(-MAX_ENTRIES);
    localStorage.setItem(CACHE_KEY, JSON.stringify(trimmed));
  } catch {
    // QuotaExceededError or other — the app works without persistence.
  }
}

export function SwrProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const isPortal = isPortalPath(pathname);
  // Start EMPTY — never call loadCache() in the initializer. The server
  // renders with an empty cache, so reading localStorage here makes the
  // client's hydration render diverge (data-gated subtrees appear that
  // the server HTML doesn't have) → React #418/#422 on every warm
  // revisit. Bit Rivky daily on /admin/service-tickets.
  const [cache] = useState<Map<string, unknown>>(() => new Map());
  const [seeded, setSeeded] = useState(false);

  // Seed the persisted cache in a mount effect, NOT during hydration.
  // Effect timing alone is NOT enough: React 18 hydrates Suspense
  // boundaries at idle priority — i.e. AFTER this effect has run — so a
  // boundary's first render would see seeded data the server HTML never
  // had → #418/#422 anyway (verified with e2e/repro-hydration.ts).
  // That's why portal children below are gated on `seeded`: they mount
  // strictly after this effect, never hydrate against server HTML, and
  // their SWR hooks see the warm cache on their very first render.
  useEffect(() => {
    if (isPortalPath(window.location.pathname)) {
      for (const [key, value] of loadCache()) {
        if (!cache.has(key)) cache.set(key, value);
      }
    }
    setSeeded(true);
  }, [cache]);

  // Persist on unload (and periodically while active, so a crash
  // doesn't lose the last 15 min of accumulated cache).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const flush = () => persistCache(cache);
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    const interval = setInterval(flush, 60_000);
    window.addEventListener("beforeunload", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      window.removeEventListener("beforeunload", flush);
      document.removeEventListener("visibilitychange", onVisibility);
      flush();
    };
  }, [cache]);

  return (
    <SWRConfig
      value={{
        // SWR's provider signature wants a typed Cache, but runtime
        // only calls Map methods (get/set/delete/keys). A plain
        // Map<string, unknown> is wire-compatible; cast via `any` to
        // shut up the generic mismatch.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        provider: () => cache as any,
        // Global defaults — individual hooks can override.
        revalidateOnFocus: false,
        dedupingInterval: 2_000,
        keepPreviousData: true,
        errorRetryCount: 2,
      }}
    >
      {/* Portal routes (admin/driver/reseller) render post-mount only:
          server and first client render both emit the splash, so there is
          nothing to hydrate-mismatch, and the cache is already seeded when
          the pages mount. Customer routes keep full SSR — no SEO cost. */}
      {isPortal && !seeded ? (
        <div className="flex min-h-screen items-center justify-center bg-gray-50">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
        </div>
      ) : (
        children
      )}
    </SWRConfig>
  );
}
